import { useState, useEffect, useCallback } from "react";
import { crDateTime, crLocalToISO } from "@/lib/dateFormat";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RichTextEditor from "@/components/RichTextEditor";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Zap, CalendarClock, Repeat, Plus, Trash2, Send, Loader2, Users2, Clock,
  CheckCircle2, XCircle, Eye, Play, Sparkles, BarChart3, Settings,
} from "lucide-react";
import EmailStatsModal from "@/components/leads/EmailStatsModal";
import EmailTrackingSettings from "@/components/leads/EmailTrackingSettings";

// Every unique lead classification the newsletter scheduler can target.
// Enrolados / En el futuro / No interesados live only 24h in the pipeline
// before being auto-archived, so segmentation uses the persistent Archived
// columns (`arch:*`) instead. The "arch:" prefix is stripped in the label so
// users just see "Activos", "En el futuro", etc.
const STATUSES = [
  // Pipeline (only the transient early stages)
  "new", "contacted", "interview_meeting", "send_info", "info_ready",
  // Introductory tab
  "introductory_class", "introductory_attended", "introductory_no_show",
  // Archived tab — persistent segmentation targets
  "arch:active", "arch:inactive", "arch:later", "arch:not_interested",
];
const CAMPUSES = ["general", "santa_ana", "atenas", "grecia", "online"];
const FOLLOWUP_ELIGIBLE = ["new", "introductory_class", "contacted", "interview_meeting",
  "send_info", "info_ready", "later"];

function fmt(iso) {
  if (!iso) return "—";
  return crDateTime(iso) || "—";
}

// =========================================================================
// SCHEDULED NEWSLETTERS TAB
// =========================================================================
function ScheduledNewslettersTab() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [statsModal, setStatsModal] = useState({ open: false, id: null, label: null });
  const [form, setForm] = useState({
    label: "",
    subject: "",
    body_html: "",
    scheduled_at: "",
    campus: "all",
    statuses: [],
    exclude_already_converted: true,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduled-newsletters");
      setItems(data.items || []);
    } catch { toast.error(t("automation_load_error")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const toggleStatus = (s) => {
    setForm((f) => ({
      ...f,
      statuses: f.statuses.includes(s) ? f.statuses.filter((x) => x !== s) : [...f.statuses, s],
    }));
  };

  const previewRecipients = async () => {
    try {
      const { data } = await api.post("/scheduled-newsletters/preview-recipients", {
        subject: form.subject || "preview",
        body_html: form.body_html || "preview",
        scheduled_at: form.scheduled_at || new Date().toISOString(),
        campus: form.campus === "all" ? null : form.campus,
        statuses: form.statuses.length ? form.statuses : null,
        exclude_already_converted: form.exclude_already_converted,
      });
      setPreview(data);
    } catch { toast.error(t("automation_preview_error")); }
  };

  const submit = async () => {
    if (!form.subject.trim() || !form.body_html.trim() || !form.scheduled_at) {
      toast.error(t("automation_required_fields"));
      return;
    }
    // Local (hora de Costa Rica) → UTC ISO
    const iso = crLocalToISO(form.scheduled_at);
    if (new Date(iso).getTime() <= Date.now()) {
      toast.error(t("automation_must_be_future"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/scheduled-newsletters", {
        subject: form.subject.trim(),
        body_html: form.body_html,
        label: form.label.trim() || null,
        scheduled_at: iso,
        campus: form.campus === "all" ? null : form.campus,
        statuses: form.statuses.length ? form.statuses : null,
        exclude_already_converted: form.exclude_already_converted,
      });
      toast.success(t("automation_scheduled_ok"));
      setShowNew(false);
      setForm({ label: "", subject: "", body_html: "", scheduled_at: "", campus: "all", statuses: [], exclude_already_converted: true });
      setPreview(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("automation_scheduled_error"));
    } finally { setSaving(false); }
  };

  const cancelItem = async (id) => {
    try {
      await api.delete(`/scheduled-newsletters/${id}`);
      toast.success(t("automation_cancelled_ok"));
      load();
    } catch { toast.error(t("automation_cancel_error")); }
  };

  const statusColor = {
    pending: "bg-amber-100 text-amber-700",
    sending: "bg-blue-100 text-blue-700",
    sent: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-slate-200 text-slate-600",
    failed: "bg-rose-100 text-rose-700",
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{t("automation_scheduled_title")}</h3>
          <p className="text-sm text-slate-500">{t("automation_scheduled_desc")}</p>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-brand-blue hover:bg-brand-blue/90" data-testid="automation-new-scheduled-btn">
          <Plus className="h-4 w-4 mr-1.5" />{t("automation_new_scheduled")}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <CalendarClock className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <p>{t("automation_scheduled_empty")}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <Card key={it.id} data-testid={`scheduled-newsletter-${it.id}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={statusColor[it.status] || "bg-slate-100"} data-testid={`sn-status-${it.id}`}>{t(`automation_sn_status_${it.status}`)}</Badge>
                    <span className="font-medium text-slate-900 truncate">{it.label || it.subject}</span>
                  </div>
                  <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                    <Clock className="h-3 w-3" />
                    <span>{fmt(it.scheduled_at)}</span>
                    {it.campus && <Badge variant="outline" className="text-xs">{t(`lead_campus_${it.campus}`)}</Badge>}
                    {it.statuses?.length > 0 && (
                      <span className="flex gap-1">
                        {it.statuses.map((s) => {
                          const isArch = String(s).startsWith("arch:");
                          const k = isArch ? `lead_archived_${String(s).slice(5)}` : `lead_status_${s}`;
                          return (
                            <Badge key={s} variant="outline" className="text-xs">{t(k)}</Badge>
                          );
                        })}
                      </span>
                    )}
                    {it.status === "sent" && (
                      <span className="text-emerald-700 font-medium">· {it.sent_count} enviados{it.failed_count ? ` / ${it.failed_count} fallidos` : ""}</span>
                    )}
                  </div>
                </div>
                {(it.status === "sent" || it.status === "sending") && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setStatsModal({ open: true, id: it.id, label: it.label || it.subject })}
                    data-testid={`sn-view-stats-${it.id}`}
                  >
                    <BarChart3 className="h-4 w-4 mr-1" />
                    {t("email_stats_view_button")}
                  </Button>
                )}
                {it.status === "pending" ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" data-testid={`sn-cancel-${it.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("automation_cancel_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("automation_cancel_desc")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("template_cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => cancelItem(it.id)} className="bg-rose-600 hover:bg-rose-700">
                          {t("automation_cancel_confirm")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" data-testid={`sn-delete-${it.id}`} title={t("automation_delete_record")}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("automation_delete_record_title")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("automation_delete_record_desc")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("template_cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => cancelItem(it.id)} className="bg-rose-600 hover:bg-rose-700">
                          {t("automation_delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="scheduled-newsletter-dialog">
          <DialogHeader>
            <DialogTitle>{t("automation_new_scheduled")}</DialogTitle>
            <DialogDescription>{t("automation_new_scheduled_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("automation_label")}</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder={t("automation_label_placeholder")} data-testid="sn-label" />
            </div>
            <div>
              <Label>{t("automation_subject")}</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="¡Hola {name}! Recordatorio de tu clase introductoria" data-testid="sn-subject" />
            </div>
            <div>
              <Label>{t("automation_body")}</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={form.body_html}
                  onChange={(v) => setForm({ ...form, body_html: v })}
                  placeholder="<p>Hola {name},</p><p>...</p>"
                  testId="sn-body"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">{t("automation_vars_hint")}</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("automation_when")}</Label>
                <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="sn-when" />
              </div>
              <div>
                <Label>{t("automation_campus")}</Label>
                <Select value={form.campus} onValueChange={(v) => setForm({ ...form, campus: v })}>
                  <SelectTrigger data-testid="sn-campus"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("lead_filter_all_campuses")}</SelectItem>
                    {CAMPUSES.map((c) => <SelectItem key={c} value={c}>{t(`lead_campus_${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("automation_status_filter")}</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {STATUSES.map((s) => {
                  const isArch = s.startsWith("arch:");
                  const labelKey = isArch ? `lead_archived_${s.slice(5)}` : `lead_status_${s}`;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => toggleStatus(s)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        form.statuses.includes(s)
                          ? "bg-brand-blue text-white border-brand-blue"
                          : "bg-white text-slate-600 border-slate-300 hover:border-brand-blue"
                      }`}
                      data-testid={`sn-toggle-status-${s}`}
                    >
                      {t(labelKey)}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-slate-500 mt-1">{t("automation_status_filter_hint")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.exclude_already_converted} onCheckedChange={(v) => setForm({ ...form, exclude_already_converted: v })} data-testid="sn-exclude-converted" />
              <Label className="cursor-pointer text-sm">{t("automation_exclude_converted")}</Label>
            </div>

            <div className="pt-2">
              <Button variant="outline" size="sm" onClick={previewRecipients} data-testid="sn-preview-btn">
                <Eye className="h-4 w-4 mr-1.5" />{t("automation_preview_recipients")}
              </Button>
              {preview && (
                <div className="mt-2 p-3 bg-slate-50 rounded-md text-sm border">
                  <div className="font-semibold text-slate-800">{t("automation_preview_total").replace("{n}", preview.total)}</div>
                  {preview.sample?.length > 0 && (
                    <ul className="mt-1 text-xs text-slate-600 space-y-0.5">
                      {preview.sample.map((r, i) => (
                        <li key={i}>· {r.name || "(sin nombre)"} — {r.email}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>{t("template_cancel")}</Button>
            <Button onClick={submit} disabled={saving} className="bg-brand-blue" data-testid="sn-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CalendarClock className="h-4 w-4 mr-1.5" />}
              {t("automation_schedule_button")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailStatsModal
        open={statsModal.open}
        onOpenChange={(o) => setStatsModal((s) => ({ ...s, open: o }))}
        campaignType="scheduled_newsletter"
        campaignId={statsModal.id}
        title={statsModal.label}
      />
    </div>
  );
}

// =========================================================================
// FOLLOW-UP RULES TAB
// =========================================================================
function FollowupRulesTab() {
  const { t } = useI18n();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rule or "new"
  const [saving, setSaving] = useState(false);
  const [previewCounts, setPreviewCounts] = useState({});
  const [statsModal, setStatsModal] = useState({ open: false, id: null, label: null });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/followup-rules");
      setRules(data.items || []);
    } catch { toast.error(t("automation_load_error")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const [form, setForm] = useState({
    name: "",
    source_status: "contacted",
    days_after: 2,
    subject: "",
    body_html: "",
    active: true,
  });

  const openNew = () => {
    setForm({ name: "", source_status: "contacted", days_after: 2, subject: "", body_html: "", active: true });
    setEditing("new");
  };
  const openEdit = (rule) => {
    setForm({
      name: rule.name || "",
      source_status: rule.source_status,
      days_after: rule.days_after,
      subject: rule.subject,
      body_html: rule.body_html,
      active: !!rule.active,
    });
    setEditing(rule);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.subject.trim() || !form.body_html.trim()) {
      toast.error(t("automation_required_fields")); return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/followup-rules", {
          name: form.name.trim(),
          source_status: form.source_status,
          days_after: parseInt(form.days_after, 10),
          subject: form.subject.trim(),
          body_html: form.body_html,
          active: form.active,
          channel: "email",
        });
      } else {
        await api.patch(`/followup-rules/${editing.id}`, {
          name: form.name.trim(),
          source_status: form.source_status,
          days_after: parseInt(form.days_after, 10),
          subject: form.subject.trim(),
          body_html: form.body_html,
          active: form.active,
        });
      }
      toast.success(t("automation_rule_saved"));
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("automation_rule_save_error"));
    } finally { setSaving(false); }
  };

  const toggleActive = async (rule) => {
    try {
      await api.patch(`/followup-rules/${rule.id}`, { active: !rule.active });
      load();
    } catch { toast.error(t("automation_rule_save_error")); }
  };

  const previewRule = async (rule) => {
    try {
      const { data } = await api.post(`/followup-rules/${rule.id}/preview`);
      setPreviewCounts((p) => ({ ...p, [rule.id]: data }));
      toast.success(t("automation_preview_ok").replace("{n}", data.matches));
    } catch { toast.error(t("automation_preview_error")); }
  };

  const deleteRule = async (rule) => {
    try {
      await api.delete(`/followup-rules/${rule.id}`);
      toast.success(t("automation_rule_deleted"));
      load();
    } catch { toast.error(t("automation_rule_delete_error")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{t("automation_rules_title")}</h3>
          <p className="text-sm text-slate-500">{t("automation_rules_desc")}</p>
        </div>
        <Button onClick={openNew} className="bg-brand-blue hover:bg-brand-blue/90" data-testid="automation-new-rule-btn">
          <Plus className="h-4 w-4 mr-1.5" />{t("automation_new_rule")}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : rules.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-slate-500">
          <Repeat className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <p>{t("automation_rules_empty")}</p>
          <p className="text-xs text-slate-400 mt-1">{t("automation_rules_example")}</p>
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {rules.map((rule) => (
            <Card key={rule.id} data-testid={`followup-rule-${rule.id}`} className={rule.active ? "" : "opacity-60"}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-slate-900">{rule.name}</span>
                      {rule.active
                        ? <Badge className="bg-emerald-100 text-emerald-700">{t("automation_active")}</Badge>
                        : <Badge className="bg-slate-200 text-slate-600">{t("automation_paused")}</Badge>}
                    </div>
                    <div className="text-sm text-slate-600">
                      {t("automation_rule_summary")
                        .replace("{status}", t(`lead_status_${rule.source_status}`))
                        .replace("{days}", rule.days_after)}
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {t("automation_rule_stats")
                        .replace("{sent}", rule.total_sent || 0)
                        .replace("{last}", rule.last_run_at ? fmt(rule.last_run_at) : "—")}
                    </div>
                    {previewCounts[rule.id] && (
                      <div className="mt-2 text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1 inline-block">
                        {t("automation_preview_ok").replace("{n}", previewCounts[rule.id].matches)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Switch checked={rule.active} onCheckedChange={() => toggleActive(rule)} data-testid={`rule-toggle-${rule.id}`} />
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => previewRule(rule)} data-testid={`rule-preview-${rule.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {(rule.total_sent || 0) > 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setStatsModal({ open: true, id: rule.id, label: rule.name })}
                          title={t("email_stats_view_button")}
                          data-testid={`rule-stats-${rule.id}`}
                        >
                          <BarChart3 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEdit(rule)} data-testid={`rule-edit-${rule.id}`}>
                        {t("automation_edit")}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="text-rose-600 hover:bg-rose-50" data-testid={`rule-delete-${rule.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t("automation_delete_rule")}</AlertDialogTitle>
                            <AlertDialogDescription>{t("automation_delete_rule_desc")}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t("template_cancel")}</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteRule(rule)} className="bg-rose-600 hover:bg-rose-700">
                              {t("automation_delete")}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("automation_new_rule") : t("automation_edit_rule")}</DialogTitle>
            <DialogDescription>{t("automation_rule_dialog_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t("automation_rule_name")}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("automation_rule_name_placeholder")} data-testid="rule-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("automation_rule_source_status")}</Label>
                <Select value={form.source_status} onValueChange={(v) => setForm({ ...form, source_status: v })}>
                  <SelectTrigger data-testid="rule-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FOLLOWUP_ELIGIBLE.map((s) => <SelectItem key={s} value={s}>{t(`lead_status_${s}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("automation_rule_days_after")}</Label>
                <Input type="number" min={1} max={365} value={form.days_after} onChange={(e) => setForm({ ...form, days_after: e.target.value })} data-testid="rule-days" />
              </div>
            </div>
            <div>
              <Label>{t("automation_subject")}</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="¿Sigues interesado, {name}?" data-testid="rule-subject" />
            </div>
            <div>
              <Label>{t("automation_body")}</Label>
              <div className="mt-1">
                <RichTextEditor
                  value={form.body_html}
                  onChange={(v) => setForm({ ...form, body_html: v })}
                  placeholder="<p>Hola {name}, hace unos días te escribimos...</p>"
                  testId="rule-body"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">{t("automation_vars_hint")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="rule-active" />
              <Label className="cursor-pointer text-sm">{t("automation_active")}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t("template_cancel")}</Button>
            <Button onClick={submit} disabled={saving} className="bg-brand-blue" data-testid="rule-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
              {t("automation_save_rule")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailStatsModal
        open={statsModal.open}
        onOpenChange={(o) => setStatsModal((s) => ({ ...s, open: o }))}
        campaignType="followup_rule"
        campaignId={statsModal.id}
        title={statsModal.label}
      />
    </div>
  );
}

// =========================================================================
// MAIN PAGE
// =========================================================================
export default function LeadAutomation() {
  const { t } = useI18n();
  return (
    <div className="space-y-6" data-testid="lead-automation-page">
      <PageHeader
        title={t("automation_page_title")}
        subtitle={t("automation_page_subtitle")}
        icon={Zap}
      />
      <Tabs defaultValue="scheduled">
        <TabsList data-testid="automation-tabs">
          <TabsTrigger value="scheduled" data-testid="tab-scheduled">
            <CalendarClock className="h-4 w-4 mr-1.5" />
            {t("automation_tab_scheduled")}
          </TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">
            <Repeat className="h-4 w-4 mr-1.5" />
            {t("automation_tab_rules")}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="h-4 w-4 mr-1.5" />
            {t("automation_tab_settings")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="scheduled" className="mt-4">
          <ScheduledNewslettersTab />
        </TabsContent>
        <TabsContent value="rules" className="mt-4">
          <FollowupRulesTab />
        </TabsContent>
        <TabsContent value="settings" className="mt-4">
          <EmailTrackingSettings />
        </TabsContent>
      </Tabs>
    </div>
  );
}
